import React, { useMemo } from 'react';
import { connect } from 'umi';
import { Segment } from 'tea-component';
import { TOptionsItem } from '../../types';
import useIntls from '@opm/hooks/useIntls';
import { GAME_CATEGORY, RawGameData, GameData } from '../../constants/games';
import { MultipleSelect, IMultipleSelectType } from '../MultipleSelect';
import {
  moreKeyCommonMapStateToProps,
  moreKeyCommonMapDispatchToProps,
} from '../../connect/moreKeyCommon';
import './index.scss';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import BubbleCommon from '@op/components/BubbleCommon';
import { gameCategoryTipsMap } from '@opm/components/BubbleCommon';

/**
 * 游戏数据数组类型（从接口获取的完整数据格式）
 */
export type RawGameListData = RawGameData;

/**
 * 游戏选择器配置类型（用户传入的 props）
 * 继承 MultipleSelect 的配置，支持直接传递其所有属性
 */
export type TGameSelectType = {
  valueKeys: {
    category: string;
    games: string;
  };
  options: RawGameListData;
  /** 固定过滤某个 genre 的游戏（专区模式），传入后隐藏分类切换器 */
  filterGenre?: string;
  /** 外部指定禁用的分类 value 列表（优先级高于自动检测） */
  disabledCategories?: string[];
  /**
   * 按数据标记禁用分类：传入 has_creative_trends / has_ugc_trends 后，
   * 若某个分类下所有游戏该项均为 false，则自动禁用该分类 Tab
   */
  disabledByDataKey?: keyof Pick<GameData, 'has_creative_trends' | 'has_ugc_trends'>;
  /** 隐藏游戏分类 Segment，保留游戏多选 */
  hideCategoryTabs?: boolean;
  /**
   * 品类切换时的回调，用于返回额外的时间筛选参数（如 start_time/end_time），
   * 返回值会合并到 change dispatch 的 payload 中，实现品类切换 + 时间修正一次 dispatch。
   *
   * @param category  - 选中的品类分类值（如 'Casual Games' 或 GAME_CATEGORY.ALL）
   * @param gameIds   - 选中的游戏 ID 列表
   * @returns 额外参数（如 { start_time, end_time }），返回 void/undefined 则不追加
   */
  onGenreChange?: (
    category: string,
    gameIds: string[],
  ) => Partial<{ start_time: string; end_time: string; date_type: string }> | void;
} & Omit<
  IMultipleSelectType,
  'valueKeys' | 'instanceId' | 'realTimeMode' | 'options'
>;

/**
 * 游戏选择器组件内部类型（包含 Redux 注入的 props）
 */
type IGameSelectProps = TGameSelectType & {
  filterValue: any;
  change: (payload: { [key: string]: any }) => void;
};

/**
 * 游戏分类选项类型
 */
type CategoryOption = {
  value: string;
  text: string;
  textCn: string;
  /** 当前分类下无游戏时禁用 */
  disabled?: boolean;
};

/**
 * 游戏分类排序顺序（按用户指定的顺序）
 */
const CATEGORY_ORDER = [
  'Casual Games',
  'Anime Games',
  'Mahjong Solitaire',
  'Arrows-like',
];

/**
 * 游戏分类 Segment 组件（带提示词）
 */
function GameCategorySegment({
  value,
  onChange,
  disabled = false,
  categoryOptions,
}: {
  value: string;
  onChange: (category: string) => void;
  disabled?: boolean;
  categoryOptions: CategoryOption[];
}) {
  const { isEn } = useIntls();

  // 转换为 Segment 需要的 options 格式
  const segmentOptions = categoryOptions.map((option) => {
    const isAllGames = option.value === GAME_CATEGORY.ALL;
    const displayText = isEn ? option.text : option.textCn;

    // All Games 不添加提示词
    if (isAllGames) {
      return {
        value: option.value,
        text: displayText,
        disabled: option.disabled,
      };
    }

    // 其他选项添加提示词
    return {
      value: option.value,
      text: (
        <BubbleCommon
          key={option.value}
          showIcon={false}
          tipsMap={gameCategoryTipsMap}
          placement="top"
        >
          {displayText}
        </BubbleCommon>
      ),
      disabled: option.disabled,
    };
  });

  return (
    <Segment
      className="segment-light"
      value={value}
      onChange={onChange}
      options={segmentOptions}
      disabled={disabled}
    />
  );
}

/**
 * 游戏选择器主组件（纯组件，不连接 Redux）
 */
export function GameSelect({
  filterValue,
  change,
  valueKeys,
  options: rawOptions,
  filterGenre,
  disabledCategories = [],
  disabledByDataKey,
  hideCategoryTabs = false,
  onGenreChange,
  ...multipleSelectProps
}: IGameSelectProps) {
  // 专区模式：按 genre 过滤 options
  const options = useMemo(() => {
    if (!filterGenre) return rawOptions || [];
    return rawOptions?.filter((game) => game.genre === filterGenre) || [];
  }, [rawOptions, filterGenre]);

  // 从 filterValue 中获取当前分类和游戏列表
  const category = filterValue?.[valueKeys.category] || GAME_CATEGORY.ALL;
  const games = filterValue?.[valueKeys.games] || [];

  // 一次遍历 options，同时构建分类选项、游戏Map和分类->游戏ID映射
  const { categoryOptions, gameMap, gamesByCategory } = useMemo(() => {
    const categoryMap = new Map<string, CategoryOption>();
    const gameMap = new Map<string, GameData>();
    const gamesByCategory = new Map<string, string[]>();
    // 记录每个分类下拥有指定数据 key 的游戏数量
    const categoryDataCount = new Map<string, number>();

    // 外部禁用分类集合，O(1) 查找
    const disabledSet = new Set(disabledCategories);

    const allGameIds: string[] = [];
    let allHasDataCount = 0;

    options.forEach((game) => {
      if (!game.game_id) return;

      // 同 game_id 只保留首次出现，避免下拉/全选出现重复项
      const isDuplicate = gameMap.has(game.game_id);
      if (!isDuplicate) {
        gameMap.set(game.game_id, game);
        allGameIds.push(game.game_id);

        // 统计「全部」分类下拥有数据 key 的游戏数
        if (disabledByDataKey && game[disabledByDataKey]) {
          allHasDataCount += 1;
        }
      }

      // 构建分类数据（排除 Others）
      const { genre } = game;
      if (genre && genre.toLocaleLowerCase() !== 'others') {
        if (!categoryMap.has(genre)) {
          categoryMap.set(genre, {
            value: genre,
            text: genre,
            textCn: game.genre_zh || genre,
          });
        }
        if (!gamesByCategory.has(genre)) {
          gamesByCategory.set(genre, []);
        }
        const categoryGameIds = gamesByCategory.get(genre)!;
        if (!categoryGameIds.includes(game.game_id)) {
          categoryGameIds.push(game.game_id);

          // 统计该分类下拥有数据的游戏数
          if (disabledByDataKey && game[disabledByDataKey]) {
            categoryDataCount.set(
              genre,
              (categoryDataCount.get(genre) || 0) + 1,
            );
          }
        }
      }
    });

    // 辅助：禁用判定
    // 优先级：外部禁用配置 > 按数据 key 全无数据禁用 > 空数据禁用
    const isDisabled = (value: string, count: number) => {
      // 外部配置优先
      if (disabledSet.has(value)) return true;
      // 空数据兜底
      if (count === 0) return true;
      // 新逻辑：按数据 key 判断
      if (disabledByDataKey) {
        const hasDataCount =
          categoryDataCount.get(value) ??
          (value === GAME_CATEGORY.ALL ? allHasDataCount : 0);
        return hasDataCount === 0;
      }
      return false;
    };

    // 全部分类
    gamesByCategory.set(GAME_CATEGORY.ALL, allGameIds);

    // 按 CATEGORY_ORDER 排序
    const sortedCategories: CategoryOption[] = [];

    sortedCategories.push({
      value: GAME_CATEGORY.ALL,
      text: 'All Games',
      textCn: '全部游戏',
      disabled: isDisabled(GAME_CATEGORY.ALL, allGameIds.length),
    });

    CATEGORY_ORDER.forEach((categoryName) => {
      if (categoryMap.has(categoryName)) {
        const opt = categoryMap.get(categoryName)!;
        const count = gamesByCategory.get(categoryName)?.length || 0;
        sortedCategories.push({ ...opt, disabled: isDisabled(categoryName, count) });
        categoryMap.delete(categoryName);
      }
    });

    categoryMap.forEach((opt) => {
      const count = gamesByCategory.get(opt.value)?.length || 0;
      sortedCategories.push({ ...opt, disabled: isDisabled(opt.value, count) });
    });

 

    return { categoryOptions: sortedCategories, gameMap, gamesByCategory };
  }, [options, disabledCategories, disabledByDataKey]);

  // 根据当前分类获取游戏列表（按 game_id 去重）
  const selectOptions: TOptionsItem[] = useMemo(() => {
    const gameIds = gamesByCategory.get(category) || [];
    const seen = new Set<string>();

    return gameIds.reduce<TOptionsItem[]>((acc, id) => {
      if (seen.has(id)) return acc;
      const game = gameMap.get(id);
      if (!game) return acc;
      seen.add(id);
      acc.push({
        value: game.game_id,
        text: game.game_name,
        textCn: game.game_name,
        image: game.cover,
      });
      return acc;
    }, []);
  }, [category, gameMap, gamesByCategory]);

  // 切换游戏分类 - 自动全选当前分类下的游戏（受 maxSelectCount 限制）
  const handleCategoryChange = (newCategory: string) => {
    // 直接从预构建的映射中获取游戏ID列表
    let newCategoryGames = gamesByCategory.get(newCategory) || [];

    // 如果有最大选中限制，截取前 N 个
    if (multipleSelectProps.maxSelectCount !== undefined) {
      newCategoryGames = newCategoryGames.slice(0, multipleSelectProps.maxSelectCount);
    }

    const basePayload = {
      [valueKeys.category]: newCategory,
      [valueKeys.games]: newCategoryGames,
    };

    // 品类切换回调：允许外部注入额外参数（如时间范围自动更新）
    const extraPayload = onGenreChange?.(newCategory, newCategoryGames);

    change({
      ...basePayload,
      ...(extraPayload || {}),
      UPDATE: true,
    });
  };

  // 默认的游戏选项渲染函数（支持 icon/image）
  const defaultItemRender = (item: TOptionsItem, isEn: boolean) => {
    const { image } = item as any;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <RenderImg
          style={{ width: 20, height: 20, borderRadius: 4 }}
          defaultImage={DefaultImg.GAME}
          coverImage={image}
        ></RenderImg>
        <span>{isEn ? item.text : item.textCn}</span>
      </div>
    );
  };

  // 使用用户传入的 itemRender 或默认的
  const itemRender = multipleSelectProps.itemRender || defaultItemRender;

  return (
    <div
      className={`dbom-game-select-wrapper ${multipleSelectProps.filterClassName || ''
        }`}
    >
      {/* 游戏分类选择器（专区模式或显式隐藏时不可见） */}
      {!filterGenre && !hideCategoryTabs && (
        <>
          <GameCategorySegment
            value={category}
            onChange={handleCategoryChange}
            categoryOptions={categoryOptions}
          />

          {/* 分隔符 */}
          <span className="game-select-separator">-</span>
        </>
      )}

      {/* 游戏多选器 */}
      <div className="dbom-game-multiple-select-inline">
        <MultipleSelect
          {...multipleSelectProps}
          options={selectOptions}
          value={games}
          change={(newGames) =>
            change({ [valueKeys.games]: newGames, UPDATE: false })
          }
          filterValue={filterValue}
          valueKeys={valueKeys.games}
          itemRender={itemRender}
          onClose={() => {
            change({ UPDATE: true });
          }}
        />
      </div>
    </div>
  );
}

/**
 * 连接 Redux 的组件
 */
export default connect(
  moreKeyCommonMapStateToProps,
  moreKeyCommonMapDispatchToProps,
)(GameSelect);
